import { PlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { errorReporter } from '@/services/errorReportingService';
import { type Subscription } from '@/services/noteGoApi/noteGoApiSchemas';

export interface ExtensionPlanData {
  features: PlanFeatures;
  monthlyVideos: number;
  storageDuration: string;
  planName: string;
  currentMonthlyUsage?: number;

  /**
   * True when the user is on a team (team-plan owner OR team member).
   * Read from userData.isTeamMember (API response) — never computed locally.
   * The extension uses this single boolean to toggle team UI — it does NOT need the org id.
   */
  isTeamMember: boolean;

  videoDuration: string;
  videoQuality: string;
  video_seconds: number;
  videoQualityNumber: number;
}

/**
 * Extension auth state received from the Chrome extension
 */
export interface ExtensionAuthState {
  hasAuth: boolean;
  token: string;
  user: {
    uid: string;
    email: string;
    displayName: string;
    photoURL: string;
  };
}

/**
 * Callback type for extension auth state listener
 */
export type ExtensionAuthCallback = (authState: ExtensionAuthState) => void;

// Store registered callbacks for extension auth state
const authStateCallbacks: Set<ExtensionAuthCallback> = new Set();

/**
 * Check if we're on a public page where extension communication should be disabled
 * Public pages don't need extension features and the extension may interfere with playback
 */
const isPublicPage = (): boolean => {
  // Check if URL matches public note pattern (/public/:token)
  // Use regex to ensure exact match and avoid false positives like /public-notes
  return /^\/public\/[^/]+$/.test(window.location.pathname);
};

/**
 * Handle incoming messages from the extension
 */
const handleExtensionMessage = (event: MessageEvent) => {
  // Skip on public pages to prevent interference
  if (isPublicPage()) {
    return;
  }

  // Only accept messages posted on this exact window from the same origin.
  // The extension content script runs in the page and posts on the same window,
  // so legitimate messages always satisfy both checks. Anything else (framing
  // pages, popups, cross-origin senders) is rejected.
  if (event.source !== window || event.origin !== window.location.origin) {
    return;
  }

  // Only process extension messages
  if (event.data?.source !== 'notego-extension') {
    return;
  }

  const { type, payload } = event.data;

  switch (type) {
    case 'EXTENSION_AUTH_STATE':
      // Extension is sending its auth state
      if (payload?.hasAuth && payload?.token) {
        authStateCallbacks.forEach((callback) => {
          try {
            callback(payload as ExtensionAuthState);
          } catch (error) {
            errorReporter.critical('Extension auth callback error', error, {
              feature: 'extension',
              operation: 'handleExtensionMessage',
            });
          }
        });
      }
      break;

    default:
      // Unknown message type
      break;
  }
};

// Initialize message listener (only once)
let listenerInitialized = false;

/**
 * Initialize the extension message listener
 * Safe to call multiple times - will only initialize once
 * Skips initialization on public pages to prevent interference with video playback
 */
export const initExtensionMessageListener = () => {
  // Don't initialize extension communication on public pages
  // This prevents "Could not establish connection" errors and video playback interference
  if (isPublicPage()) {
    // If we navigated from a non-public page to a public page in an SPA session,
    // ensure any previously registered listener is cleaned up.
    if (listenerInitialized) {
      window.removeEventListener('message', handleExtensionMessage);
      listenerInitialized = false;
      authStateCallbacks.clear();
    }
    return;
  }

  if (listenerInitialized) {
    return;
  }

  window.addEventListener('message', handleExtensionMessage);
  listenerInitialized = true;
};

/**
 * Cleanup the extension message listener
 * Removes the global message event listener and resets the initialization flag.
 * Useful for testing and hot module replacement.
 */
export const cleanupExtensionMessageListener = () => {
  if (listenerInitialized) {
    window.removeEventListener('message', handleExtensionMessage);
    listenerInitialized = false;
    authStateCallbacks.clear();
  }
};

/**
 * Register a callback for extension auth state updates
 * @param callback Function to call when extension sends auth state
 * @returns Cleanup function to unregister the callback
 */
export const onExtensionAuthState = (callback: ExtensionAuthCallback): (() => void) => {
  // Don't register callbacks on public pages - extension communication is disabled
  if (isPublicPage()) {
    return () => {}; // Return no-op cleanup function
  }

  // Ensure listener is initialized
  initExtensionMessageListener();

  authStateCallbacks.add(callback);

  // Request auth state from extension
  requestAuthStateFromExtension();

  // Return cleanup function
  return () => {
    authStateCallbacks.delete(callback);
  };
};

/**
 * Request auth state from the extension
 * The extension will respond with EXTENSION_AUTH_STATE if authenticated
 * Skips on public pages to prevent interference
 */
export const requestAuthStateFromExtension = () => {
  if (isPublicPage()) {
    return;
  }

  window.postMessage(
    {
      type: 'REQUEST_EXTENSION_AUTH',
    },
    window.location.origin,
  );
};

export const sendPlanFeaturesToExtension = (planData: ExtensionPlanData) => {
  if (isPublicPage()) {
    return;
  }

  window.postMessage(
    {
      type: 'UPDATE_PLAN_FEATURES',
      planData,
    },
    window.location.origin,
  );
};

export const sendRecordingLimitReached = (limitReached: boolean) => {
  if (isPublicPage()) {
    return;
  }

  window.postMessage(
    {
      type: 'RECORDING_LIMIT_STATUS',
      limitReached,
    },
    window.location.origin,
  );
};

export const calculateRecordingLimitReached = (
  currentMonthlyUsage: number,
  monthlyVideoLimit: number,
  isKnownUnlimitedPlan: boolean = false,
): boolean => {
  // monthlyVideoLimit === 0 is overloaded: it's the "unlimited" value AND the
  // fallback sentinel useCurrentPlanFeatures returns when pricing data is missing.
  // Only treat 0 as unlimited when the caller confirms the plan is fully resolved;
  // otherwise fail closed so a pricing outage doesn't bypass quota.
  if (monthlyVideoLimit === 0) return !isKnownUnlimitedPlan;
  return currentMonthlyUsage >= monthlyVideoLimit;
};

export const sendUserSubscriptionToExtension = (subscription: Subscription | null, currentMonthlyUsage: number) => {
  if (isPublicPage()) {
    return;
  }

  window.postMessage(
    {
      type: 'UPDATE_USER_SUBSCRIPTION',
      subscription,
      currentMonthlyUsage,
    },
    window.location.origin,
  );
};

/**
 * Open a specific page within the extension
 * @param path - The path within the extension (e.g., 'source/permissions.html')
 * @param openInNewTab - Whether to open in a new tab (true) or current window (false)
 */
export const openExtensionPage = (path: string, openInNewTab: boolean = false) => {
  if (isPublicPage()) {
    return;
  }

  window.postMessage(
    {
      type: 'OPEN_EXTENSION_PAGE',
      path,
      openInNewTab,
    },
    window.location.origin,
  );
};

/**
 * Send a session token to the extension for session-based auth.
 * The extension stores this token and uses it for API calls instead of Firebase tokens.
 */
export const sendSessionToExtension = (
  sessionToken: string,
  expiresAt: string,
  user: { uid: string; email: string | null; userDocId: string | null },
): void => {
  if (isPublicPage()) {
    return;
  }

  window.postMessage(
    {
      type: 'STORE_SESSION',
      source: 'notego-web',
      sessionToken,
      expiresAt,
      user,
    },
    window.location.origin,
  );
};

/**
 * Send logout message to the extension
 * Clears the extension's auth state when user logs out of the web app
 * Sends both CLEAR_TOKEN (legacy) and CLEAR_SESSION (new) for backwards compatibility
 */
export const sendLogoutToExtension = (): void => {
  if (isPublicPage()) {
    return;
  }

  // Legacy message for older extension versions
  window.postMessage(
    {
      type: 'CLEAR_TOKEN',
      source: 'notego-web',
    },
    window.location.origin,
  );

  // New session-based message for updated extension versions
  window.postMessage(
    {
      type: 'CLEAR_SESSION',
      source: 'notego-web',
    },
    window.location.origin,
  );
};
